// @flow
import React, { useState, useEffect } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import * as townActionCreators from "../../action-creators/town-actions";
import * as modalActionCreators from "../../action-creators/modal-actions";
import * as celebrationActionCreators from "../../action-creators/celebration-actions";
import * as supplyDistributionSiteActionCreators from "../../action-creators/supply-distribution-site-actions";
import * as trashCollectionSiteActionCreators from "../../action-creators/trash-collection-site-actions";
import * as modals from "../../constants/modals";
import * as R from "ramda";
import "./styles.css";
import { Link } from "react-router-dom";
import Page from "../../components/page";
import TextBox from "../../components/text-box";
import TextBlock from "../../components/text-block";
import TownSiteEditor from "../../components/town-site-editor";
import Town from "../../models/town";
import Checkbox from "../../components/checkbox";
import { search } from "../../libs/utilities";
import queryString from "query-string";
import { MetaData } from "../../components/meta-data";
import SearchBar from "../../components/search-bar";
import type React$Element from "react";
import { Accordion, AccordionPanel } from "../../components/accordion/accordian";
import classNames from "classnames";
import FAB from "../../components/fab";
import TrashCollectionSite from "../../models/trash-collection-site";
import SupplyDistributionSite from "../../models/supply-distribution-site";
import CelebrationsEditor from "../../components/celebrations-editor";

type RowType = { id: string, name: string };

type PropsType = {
    actions: { [string]: any => void },
    panel: ?Object,
    towns: Array<Town>,
    datum: ?Town,
    error: ?string,
    match: ?Object,
    trashCollectionSites?: Object,
    supplyDistributionSites?: Object,
    celebrations?: Object
};


const MyScreen = ({ actions, towns, match, trashCollectionSites, supplyDistributionSites, celebrations }: PropsType) => {
    const [currentTown, setCurrentTown] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const handleCurrentTownChange = R.curry((key, value) => {
        setCurrentTown({ ...currentTown, [key]: value });
        setIsDirty(true);
    });


    const RowTemplate = ({ id, name }: RowType): React$Element<any> => (
        <Link
            className={ classNames("towns--row", { "is-selected": id === match.params.id }) }
            to={ `/towns/${ id }${ window.location.search }` }>
            <span className="towns--town-name">{ name }</span>
        </Link>
    );

    const q = queryString.parse(window.location.search);
    const myData = search(towns, q.search);
    const filterSites = R.filter(site => site.townId === currentTown.id);

    useEffect(() => {
        if (currentTown.id !== match.params.id) {
            const newTown = (towns || []).find(town => town.id === match.params.id) || {};
            setCurrentTown(R.clone(newTown));
        }
        if (!match.params.id) {
            actions.closeModal(modals.MAIN_SIDE_PANEL);
        }
        setIsDirty(false);
    }, [match]);

    return (

        <Page
            RowTemplate={ RowTemplate }
            data={ myData }
            pagination={ false }
            toolbar={ (<SearchBar/>) }
        >
            <MetaData
                created={ currentTown.created }
                id={ currentTown.id }
                updated={ currentTown.updated }
            />

            <Accordion id={ "townInfo" } openExclusive={ false } initialState={ { details: true } }>
                <AccordionPanel id={ "details" } title={ "Town Details" }>
                    <header className={ "towns-screen--towns-header" }>
                        <FAB
                            className={ "towns-screen--header-add-button" }
                            icon={ "faSave" }
                            onClick={ () => {
                                actions.saveTown(currentTown);
                                setIsDirty(false);
                            } }
                            disabled={ !isDirty }/>
                    </header>
                    <TextBox
                        labelText={ "Name" }
                        onChange={ handleCurrentTownChange("name") }
                        value={ currentTown.name }
                    />

                    <TextBlock
                        className="towns--town-description"
                        style={ { minHeight: "15rem" } }
                        isValidating={ false }
                        labelText="Town Description"
                        onChange={ handleCurrentTownChange("description") }
                        required={ false }
                        value={ currentTown.description || "" }
                    />

                    <TextBlock
                        className="towns--town-notes"
                        isValidating={ false }
                        labelText="Comments"
                        onChange={ handleCurrentTownChange("notes") }
                        required={ false }
                        value={ currentTown.notes || "" }
                    />
                    <Checkbox
                        checked={ Boolean(currentTown.roadsideDropOffAllowed) }
                        labelText={ "Roadside Pickup Permitted" }
                        onChange={ () => {
                            handleCurrentTownChange("roadsideDropOffAllowed")(!currentTown.roadsideDropOffAllowed);
                        } }
                    />
                    <TextBlock
                        className="towns--town-drop-off-instructions"
                        isValidating={ false }
                        labelText="Drop Off Instructions"
                        onChange={ handleCurrentTownChange("dropOffInstructions") }
                        required={ false }
                        value={ currentTown.dropOffInstructions || "" }
                    />
                    <TextBlock
                        className="towns--town-pickup-instructions"
                        isValidating={ false }
                        labelText="Pick Up Instructions"
                        onChange={ handleCurrentTownChange("pickupInstructions") }
                        required={ false }
                        value={ currentTown.pickupInstructions || "" }
                    />
                    <br/>
                </AccordionPanel>
                <AccordionPanel id={ "disposal" } title={ "Trash Collection Sites" }>
                    <TownSiteEditor
                        Model={ TrashCollectionSite }
                        locations={ filterSites(trashCollectionSites.data) }
                        townId={ currentTown.id }
                        onDelete={ actions.deleteTrashCollectionSite }
                        onSave={ foo => {
                            actions.saveTrashCollectionSite(foo);
                        } }
                    />
                </AccordionPanel>
                <AccordionPanel id={ "supplies" } s={ true } title={ "Supplies Distribution Sites" }>
                    <TownSiteEditor
                        Model={ SupplyDistributionSite }
                        locations={ filterSites(supplyDistributionSites.data) }
                        townId={ currentTown.id }
                        onDelete={ actions.deleteSupplyDistributionSite }
                        onSave={ actions.saveSupplyDistributionSite }
                    />
                </AccordionPanel>
                <AccordionPanel id={ "celebrations" } title={ "Celebrations" }>
                    <CelebrationsEditor
                        townId={ currentTown.id }
                        celebrations={ filterSites(celebrations.data) }
                        onDelete={ actions.deleteCelebration }
                        onSave={ actions.saveCelebration }
                    />
                </AccordionPanel>
            </Accordion>

        </Page>

    );
};


const mapStateToProps = (state: Object): Object => {
    const towns = Object.values((state.towns || {}).data || {});
    const trashCollectionSites = state.trashCollectionSites;
    const supplyDistributionSites = state.supplyDistributionSites;
    const celebrations = state.celebrations;
    return { towns, trashCollectionSites, supplyDistributionSites, celebrations };
};

const mapDispatchToProps = (dispatch): Object => {
    const myActions = {
        ...townActionCreators,
        ...modalActionCreators,
        ...celebrationActionCreators,
        ...trashCollectionSiteActionCreators,
        ...supplyDistributionSiteActionCreators
    };
    return { actions: bindActionCreators(myActions, dispatch) };
};

export const TownsScreen = connect(mapStateToProps, mapDispatchToProps)(MyScreen);

